import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import React, { useCallback, useState } from 'react';

import {
  QBox,
  QButton,
  QDivider,
  QIcon,
  QMenu,
  QMenuItem,
  QNestedMenuItem,
  QText,
  useTheme,
} from '@captivateiq/quotient';

import type { ModelOption, ReasoningEffort } from '../config/modelConfig';
import { ALL_MODELS, DEFAULT_MODEL, EFFORT_OPTIONS } from '../config/modelConfig';

const EFFORT_LABELS: Record<ReasoningEffort, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

type ModelSelectorProps = {
  selectedModel: ModelOption;
  onModelChange: (model: ModelOption) => void;
  selectedEffort?: ReasoningEffort;
  onEffortChange?: (effort: ReasoningEffort) => void;
  variant?: 'inline';
};

export const ModelSelector = ({
  selectedModel,
  onModelChange,
  selectedEffort,
  onEffortChange,
  variant,
}: ModelSelectorProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const [isEffortHovered, setIsEffortHovered] = useState(false);

  const handleToggle = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
    setIsEffortHovered(false);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setIsEffortHovered(false);
  }, []);

  const handleSelect = useCallback(
    (model: ModelOption) => {
      onModelChange(model);
      setAnchorEl(null);
      setIsEffortHovered(false);
    },
    [onModelChange],
  );

  const handleEffortSelect = useCallback(
    (effort: ReasoningEffort) => {
      onEffortChange?.(effort);
      setAnchorEl(null);
      setIsEffortHovered(false);
    },
    [onEffortChange],
  );

  const inlineTrigger = (
    <QBox
      component="button"
      data-testid="model-selector-trigger"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '12px',
        fontWeight: 400,
        color: 'text.onSurface.background.mediumContrast',
        padding: '2px 5px',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': { bgcolor: 'action.hover' },
      }}
      type="button"
      onClick={handleToggle}
    >
      {selectedModel.label}
      {selectedEffort && <QBox sx={{ opacity: 0.6 }}>{EFFORT_LABELS[selectedEffort]}</QBox>}
      <QIcon icon={faChevronDown} sx={{ fontSize: '8px', opacity: 0.5 }} />
    </QBox>
  );

  return (
    <>
      {variant === 'inline' ? (
        inlineTrigger
      ) : (
        <QButton
          data-testid="model-selector-trigger"
          endIcon={faChevronDown}
          size="small"
          variant="outlined"
          onClick={handleToggle}
        >
          {selectedModel.label}
        </QButton>
      )}
      <QMenu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        open={isOpen}
        slotProps={{ paper: { sx: { width: '240px' } } }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleClose}
      >
        {ALL_MODELS.map((model) => (
          <QMenuItem
            chip={model.id !== DEFAULT_MODEL.id ? 'Dev' : undefined}
            data-testid={`model-option-${model.id}`}
            key={model.id}
            leftIcon={selectedModel.id === model.id ? faCheck : undefined}
            onClick={() => handleSelect(model)}
          >
            <QBox>
              <QText sx={{ fontSize: '13px', fontWeight: selectedModel.id === model.id ? 600 : 400 }}>
                {model.label}
              </QText>
              <QText sx={{ fontSize: '11px', color: 'grey.500' }}>{model.description}</QText>
            </QBox>
          </QMenuItem>
        ))}
        {selectedEffort && onEffortChange && (
          <>
            <QDivider sx={{ my: '4px' }} />
            <QNestedMenuItem
              ContainerProps={{
                onMouseEnter: () => setIsEffortHovered(true),
                onMouseLeave: () => setIsEffortHovered(false),
                style: { backgroundColor: isEffortHovered ? theme.palette.action.hover : undefined },
              }}
              data-testid="effort-menu-item"
              label="Effort"
              MenuProps={{
                anchorOrigin: { horizontal: 'left', vertical: 'top' },
                transformOrigin: { horizontal: 'right', vertical: 'top' },
                slotProps: { paper: { sx: { width: '120px' } } },
              }}
              parentMenuOpen={isOpen}
              renderLabel={() => (
                <QBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <QText sx={{ fontSize: '13px' }}>Effort</QText>
                  <QText sx={{ fontSize: '13px', color: 'grey.500' }}>{EFFORT_LABELS[selectedEffort]}</QText>
                </QBox>
              )}
              rightIcon={
                <QIcon
                  icon={faChevronRight}
                  sx={{ fontSize: '10px', color: 'text.onSurface.background.mediumContrast' }}
                />
              }
            >
              {EFFORT_OPTIONS.map((effort) => (
                <QMenuItem
                  data-testid={`effort-option-${effort}`}
                  key={effort}
                  rightIcon={selectedEffort === effort ? faCheck : undefined}
                  onClick={() => handleEffortSelect(effort)}
                >
                  <QText sx={{ fontSize: '13px', fontWeight: selectedEffort === effort ? 600 : 400 }}>
                    {EFFORT_LABELS[effort]}
                  </QText>
                </QMenuItem>
              ))}
            </QNestedMenuItem>
          </>
        )}
      </QMenu>
    </>
  );
};
